import { BookmarkConfiguration } from '../BookmarkConfiguration';
import { BookmarkService } from '../BookmarkService';
import { PlaybackSessionBookmarker } from '../PlaybackSessionBookmarker';
import { createBookmarkServiceImpl } from '../bookmarkService/internal/BookmarkServiceImpl';
import { createEventEmitter, createLogger, EventEmitter, Logger, LoggerLevel } from '@quickplay/foundation';
import {
  AdComposedPlayer,
  AdTechnology,
  EpisodeDetails,
  PLAYBACK_STATES,
  Player,
  PlayerContext,
} from '@quickplay/player-interface';
import { PlatformAuthorizer } from '@quickplay/platform-authorizer';
import { Mode } from '../bookmarkService/internal/BookmarkRequest';

const TAG_NAME = 'FL_BOOKMARKS';

function instanceOfAdComposedPlayer(player: Player): player is AdComposedPlayer {
  return (player as AdComposedPlayer).playerContext !== undefined;
}

function shouldUpdateBookmark(player: Player): boolean {
  const isAdComposedPlayer = instanceOfAdComposedPlayer(player);

  if (!isAdComposedPlayer || player.adInsertionType !== AdTechnology.CSAI) {
    return true;
  }

  return player.playerContext === PlayerContext.MAIN;
}

export function createPlaybackSessionBookmarkerImpl(
  contentId: string,
  bookmarkConfiguration: BookmarkConfiguration,
  platformAuthorizer: PlatformAuthorizer,
  seasonId?: string,
  episodeId?: string,
  mode?: Mode,
  videoId?: string,
  seasonNumber?: number,
  episodeNumber?: number,
  nextEpisode?: EpisodeDetails,
  contentType?: string,
  logger?: Logger,
  bookmarkService: BookmarkService = createBookmarkServiceImpl(
    bookmarkConfiguration.endPointUrl,
    platformAuthorizer,
    logger,
  ),
): PlaybackSessionBookmarker {
  const eventEmitter: EventEmitter = createEventEmitter();
  const bookmarkLogger: Logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
  let thresholdLimitReached = false;
  let interval: any;
  const bookmarkSyncIntervalInMS = bookmarkConfiguration.syncIntervalMS || 5000;
  const bookmarkThresholdPercentage = bookmarkConfiguration.thresholdPercentage || 0.95;
  const bookmarkThresholdPlaybackPositionMs = bookmarkConfiguration.thresholdPlaybackPositionMs;
  const internalBookmarkService = bookmarkService;
  let nextEpisodeDetails: EpisodeDetails | undefined = nextEpisode;

  const playbackSessionBookmarker: PlaybackSessionBookmarker = {
    subscribe: eventEmitter.subscribe,
    unsubscribe: eventEmitter.unsubscribe,

    get contentId(): string {
      return contentId;
    },

    get seasonId(): string | undefined {
      return seasonId;
    },

    get episodeId(): string | undefined {
      return episodeId;
    },

    get bookmarkService(): BookmarkService {
      return internalBookmarkService;
    },

    get isThresholdReached(): boolean {
      return thresholdLimitReached;
    },

    setNextEpisodeDetails(episodeDetails: EpisodeDetails): void {
      nextEpisodeDetails = episodeDetails;
    },

    processPlayerStateChange(player: Player): void {
      // bookmarks should never be fired for live streams
      if (player.isLive) {
        return;
      }

      switch (player.playerState) {
        case PLAYBACK_STATES.STARTED: {
          startTimer(player);
          break;
        }
        case PLAYBACK_STATES.PAUSED:
        case PLAYBACK_STATES.IDLE: {
          // In chromecast, when playback is interrupted by switching to other content, player goes to idle state.
          // In this case, the playback position in chromecast becomes 0, which is not the actual playback position. So, we should not update bookmark if the player is in idle state to avoid updating bookmark with 0 offset.
          // For other platforms, we can update bookmark when player is in paused or idle state.
          player.currentTime !== 0 && updateBookmark(player);
          stopTimer();
          break;
        }
        default:
          break;
      }
    },
  };

  function startTimer(player: Player): void {
    if (interval) return;
    interval = setInterval(() => {
      updateBookmark(player);
    }, bookmarkSyncIntervalInMS);
  }

  function stopTimer(): void {
    clearInterval(interval);
    interval = undefined;
  }

  function isThresholdLimitReached(currentOffsetInMS: number, videoDurationInMs: number): boolean {
    if (bookmarkThresholdPercentage && bookmarkThresholdPlaybackPositionMs) {
      const playbackPositionThresholdInMs = Math.min(
        videoDurationInMs * bookmarkThresholdPercentage,
        bookmarkThresholdPlaybackPositionMs,
      );
      return currentOffsetInMS >= playbackPositionThresholdInMs;
    }
    if (bookmarkThresholdPlaybackPositionMs) {
      return currentOffsetInMS >= bookmarkThresholdPlaybackPositionMs;
    }

    return currentOffsetInMS >= bookmarkThresholdPercentage * videoDurationInMs;
  }

  function deleteBookmark(): void {
    if (!thresholdLimitReached) {
      const episodeID = mode === Mode.PLAYLIST ? videoId : episodeId;

      internalBookmarkService.deleteBookmark(contentId, mode, episodeID);

      if (nextEpisodeDetails) {
        internalBookmarkService.putBookmark(
          nextEpisodeDetails.itemId,
          /* offset */ 0,
          nextEpisodeDetails.seasonId,
          nextEpisodeDetails.episodeId,
          nextEpisodeDetails.mode,
          nextEpisodeDetails.videoId,
          nextEpisodeDetails.seasonNumber,
          nextEpisodeDetails.episodeNumber,
          nextEpisodeDetails.contentType,
        );

        bookmarkLogger.log('[BOOKMARKS] put bookmark for next episode', nextEpisodeDetails);
      }

      thresholdLimitReached = true;
    }
  }

  function updateBookmark(player: Player): void {
    // Developer Notes:
    // Prevent updating bookmarks during CSAI ad playback because the reported time belongs to the ad, not the content.
    if (!shouldUpdateBookmark(player)) {
      return;
    }

    if (isThresholdLimitReached(player.currentTime * 1000, player.duration * 1000)) {
      deleteBookmark();
      eventEmitter.publish('thresholdlimitreached');
    } else {
      // Developer Notes:
      // When bookmark is deleted after reaching the threshold limit and user seeks back
      // and pauses the content, we need to enable update and delete bookmark again.
      thresholdLimitReached = false;
      internalBookmarkService.putBookmark(
        contentId,
        player.currentTime,
        seasonId,
        episodeId,
        mode,
        videoId,
        seasonNumber,
        episodeNumber,
        contentType,
      );
    }
  }

  return playbackSessionBookmarker;
}
